// mirror of auware/.../frontend/src/pages/auth/LoginPage.tsx (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations:
//  - Brand strings + logo swapped.
//  - "Sign up" link removed - SPEC §5 has no public signup.
//  - Handles SPEC §5 password-setup-required (403 with
//    error="password_setup_required") by showing a "Check your inbox"
//    banner (the backend has already sent the setup email).
//  - Handles SPEC §5 MFA-required-for-agents (403 with
//    error="mfa_required") by telling the user to contact an admin -
//    they can't reach Settings without 2FA.
//  - Cookie names: `buyist-passkey`, `buyist-remember`.

import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

import Button from "@/components/ui/Button";
import { useAuthStore } from "@/stores/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

const PASSKEY_COOKIE = "buyist-passkey";
const REMEMBER_COOKIE = "buyist-remember";

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function clearCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractUser(data: Record<string, unknown>) {
  const u = data.user as Record<string, unknown>;
  return {
    id: u.id as string,
    email: u.email as string,
    name: u.full_name as string,
    avatarUrl: u.avatar_url as string | undefined,
    role: u.role as "customer" | "agent" | "system_admin" | undefined,
    isSystemAdmin: u.is_system_admin as boolean | undefined,
    isAgent: u.is_agent as boolean | undefined,
    mfaEnabled: u.mfa_enabled as boolean | undefined,
    hasPasskeys: u.has_passkeys as boolean | undefined,
    isEmailVerified: u.is_email_verified as boolean | undefined,
  };
}

function extractTokens(data: Record<string, unknown>) {
  return {
    accessToken: data.access_token as string,
    refreshToken: data.refresh_token as string,
  };
}

interface ApiError {
  response?: {
    status?: number;
    data?: { detail?: string | { error?: string; message?: string; redirect?: string } };
  };
}

function detailMessage(err: unknown, fallback: string): string {
  const detail = (err as ApiError)?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && detail.message) {
    return detail.message;
  }
  return fallback;
}

function detailError(err: unknown): string | null {
  const detail = (err as ApiError)?.response?.data?.detail;
  if (detail && typeof detail === "object") return detail.error ?? null;
  return null;
}

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------

export function LoginPage(): JSX.Element {
  usePageTitle("Log in");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);

  // Read redirect URL. Only accept relative paths (open-redirect guard).
  const rawRedirect = searchParams.get("redirect");
  const redirectTarget =
    rawRedirect && rawRedirect.startsWith("/") ? rawRedirect : "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [rememberDevice, setRememberDevice] = useState(
    () => getCookie(REMEMBER_COOKIE) !== "0",
  );
  const [usePasskey, setUsePasskey] = useState(
    () => getCookie(PASSKEY_COOKIE) === "1",
  );

  // 2FA challenge state
  const [requires2FA, setRequires2FA] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [totpCode, setTotpCode] = useState("");

  // Passkey state
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPasskeyRegPrompt, setShowPasskeyRegPrompt] = useState(false);
  const [passkeyRegLoading, setPasskeyRegLoading] = useState(false);
  const pendingAuthRef = useRef<{
    user: ReturnType<typeof extractUser>;
    tokens: ReturnType<typeof extractTokens>;
  } | null>(null);
  const autoTriggeredRef = useRef(false);

  const webauthnSupported =
    typeof window !== "undefined" && !!window.PublicKeyCredential;

  // -- Passkey authentication ------------------------------------------------
  const triggerPasskeyAuth = useCallback(async () => {
    setError(null);
    setPasskeyLoading(true);
    try {
      const { data: optionsResponse } = await api.post(
        "/auth/passkeys/authenticate/options",
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const credential = await startAuthentication({
        optionsJSON: optionsResponse.options as any,
      });

      const { data } = await api.post("/auth/passkeys/authenticate/verify", {
        credential,
      });

      const user = extractUser(data);
      const tokens = extractTokens(data);
      login(user, tokens);

      setCookie(PASSKEY_COOKIE, "1", 365);

      if (!user.isEmailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError(null);
      } else {
        const message = detailMessage(err, "");
        if (!autoTriggeredRef.current || message) {
          setError(message || "Passkey authentication failed.");
        }
      }
    } finally {
      setPasskeyLoading(false);
    }
  }, [login, navigate, redirectTarget]);

  // Auto-trigger passkey auth if cookie is set
  useEffect(() => {
    if (
      webauthnSupported &&
      getCookie(PASSKEY_COOKIE) === "1" &&
      !autoTriggeredRef.current
    ) {
      autoTriggeredRef.current = true;
      triggerPasskeyAuth();
    }
  }, [webauthnSupported, triggerPasskeyAuth]);

  // -- Password login --------------------------------------------------------
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", { email, password });

      if (rememberDevice) {
        clearCookie(REMEMBER_COOKIE);
      } else {
        setCookie(REMEMBER_COOKIE, "0", 365);
      }

      if (data.requires_2fa) {
        setRequires2FA(true);
        setChallengeToken(data.mfa_challenge_token);
        setLoading(false);
        return;
      }

      const user = extractUser(data);
      const tokens = extractTokens(data);

      if (usePasskey && webauthnSupported && !user.hasPasskeys) {
        pendingAuthRef.current = { user, tokens };
        login(user, tokens);
        setLoading(false);
        setShowPasskeyRegPrompt(true);
        return;
      }

      login(user, tokens);

      if (!user.isEmailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err: unknown) {
      const code = detailError(err);
      // SPEC §5 customer first-login: backend already emailed a setup
      // link, just nudge the user to check their inbox.
      if (code === "password_setup_required") {
        setInfo(
          detailMessage(
            err,
            "We've sent you a link to set up your password. Please check your inbox.",
          ),
        );
        setLoading(false);
        return;
      }
      // SPEC §5 agent without MFA. They can't bootstrap themselves; tell
      // them to contact an admin (chicken-and-egg - they need 2FA to
      // reach Settings, but they need to log in to set up 2FA).
      if (code === "mfa_required") {
        setError(
          "Your account requires two-factor authentication, but it has not been set up yet. Please contact a Buyist Support admin to enable 2FA on your account.",
        );
        setLoading(false);
        return;
      }
      setError(
        detailMessage(err, "Invalid email or password. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  // -- 2FA verification ------------------------------------------------------
  const handle2FASubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login/verify-2fa", {
        challenge_token: challengeToken,
        code: totpCode,
      });

      const user = extractUser(data);
      const tokens = extractTokens(data);

      if (usePasskey && webauthnSupported && !user.hasPasskeys) {
        pendingAuthRef.current = { user, tokens };
        login(user, tokens);
        setLoading(false);
        setShowPasskeyRegPrompt(true);
        return;
      }

      login(user, tokens);

      if (!user.isEmailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err: unknown) {
      setError(
        detailMessage(err, "Invalid verification code. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  // -- Post-login passkey registration --------------------------------------
  const handlePasskeyRegistration = async () => {
    setPasskeyRegLoading(true);
    setError(null);
    try {
      const { data: optionsData } = await api.post(
        "/auth/passkeys/register/options",
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const credential = await startRegistration({
        optionsJSON: optionsData.options as any,
      });

      await api.post("/auth/passkeys/register/verify", { credential });

      setCookie(PASSKEY_COOKIE, "1", 365);

      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.setState({
          user: { ...currentUser, hasPasskeys: true },
        });
      }

      const user = pendingAuthRef.current?.user;
      if (user && !user.isEmailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError(null);
      } else {
        setError(
          "Passkey registration failed. You can set one up later in Settings.",
        );
      }
      const user = pendingAuthRef.current?.user;
      if (user && !user.isEmailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    } finally {
      setPasskeyRegLoading(false);
      setShowPasskeyRegPrompt(false);
      pendingAuthRef.current = null;
    }
  };

  const skipPasskeyRegistration = () => {
    setShowPasskeyRegPrompt(false);
    const user = pendingAuthRef.current?.user;
    pendingAuthRef.current = null;
    if (user && !user.isEmailVerified) {
      navigate("/verify-email", { replace: true });
    } else {
      navigate(redirectTarget, { replace: true });
    }
  };

  // -- Passkey registration prompt ------------------------------------------
  if (showPasskeyRegPrompt) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img
              src="/buyist-logo.png"
              alt="Buyist Support"
              className="mx-auto h-14 w-auto"
            />
            <div className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
              <i
                className="fa-duotone fa-thin fa-fingerprint text-3xl text-primary-500"
                aria-hidden="true"
              />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-neutral-900">
              Set up passkey
            </h2>
            <p className="mt-2 text-sm text-neutral-500">
              Use Face ID, Touch ID, or your security key for faster sign-in
              next time.
            </p>
          </div>

          <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              className="w-full"
              onClick={handlePasskeyRegistration}
              isLoading={passkeyRegLoading}
            >
              <i
                className="fa-duotone fa-thin fa-fingerprint"
                aria-hidden="true"
              />
              Create Passkey
            </Button>

            <button
              type="button"
              onClick={skipPasskeyRegistration}
              className="w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -- 2FA verification form ------------------------------------------------
  if (requires2FA) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img
              src="/buyist-logo.png"
              alt="Buyist Support"
              className="mx-auto h-14 w-auto"
            />
            <h2 className="mt-2 text-xl font-semibold text-neutral-900">
              Two-factor authentication
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <form
            onSubmit={handle2FASubmit}
            className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
          >
            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="totp-code"
                className="block text-sm font-medium text-neutral-700"
              >
                Verification code
              </label>
              <input
                id="totp-code"
                name="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/\D/g, ""))
                }
                className="mt-1 block w-full rounded-lg border-neutral-300 text-center text-2xl tracking-[0.3em] shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="000000"
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" isLoading={loading}>
              Verify
            </Button>

            <button
              type="button"
              onClick={() => {
                setRequires2FA(false);
                setChallengeToken("");
                setTotpCode("");
                setError(null);
              }}
              className="w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // -- Standard login form ---------------------------------------------------
  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <h2 className="mt-2 text-xl font-semibold text-neutral-900">
            Sign in to Buyist Support
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Use the email address you receive Buyist Support emails at.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {info && (
            <div className="rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-700">
              {info}
            </div>
          )}

          {passkeyLoading && (
            <div className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              <p className="text-sm text-primary-700">Requesting passkey...</p>
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-neutral-700"
            >
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-neutral-700"
              >
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Your password"
            />
          </div>

          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-600">
                Remember this device
              </span>
            </label>

            {webauthnSupported && (
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={usePasskey}
                  onChange={(e) => setUsePasskey(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-600">Use Passkey</span>
              </label>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            isLoading={loading}
            disabled={passkeyLoading}
          >
            Sign in
          </Button>

          {webauthnSupported && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-neutral-400">or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                isLoading={passkeyLoading}
                disabled={loading}
                onClick={triggerPasskeyAuth}
              >
                <i
                  className="fa-duotone fa-thin fa-fingerprint"
                  aria-hidden="true"
                />
                Sign in with Passkey
              </Button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
